import { observable } from 'mobx';
import type { MileageNumberArgs } from '@this/domain/mileage_number';
import MileageNumber from '@this/domain/mileage_number';
import type {
  TravelerShinkansenTypeArgs,
  TravelerShinkansenTypeResponseArgs
} from './traveler/traveler_shinkansen_type';
import TravelerShinkansenType, {
  convertTravelerShinkansenTypeResponseToArgs
} from './traveler/traveler_shinkansen_type';

export type TravelerInformationResponseArgs = {
  id: number;
  name: string;
  email?: string;
  user_id?: number;
  user_name?: string;
  last_name_kana?: string;
  first_name_kana?: string;
  last_name_roman?: string;
  first_name_roman?: string;
  air_seat?: string;
  shinkansen_seat?: string;
  shinkansen_seat_type_text?: string;
  shinkansen_ticket_type_text?: string;
  show_railway_seat?: boolean;
  traveler_information_shinkansen_types?: TravelerShinkansenTypeResponseArgs[];
  shinkansen_seat_type?: string;
  shinkansen_ticket_type?: string;
  exic_number?: string;
  exic_password?: string;
  mileage_number?: string;
  mileage_numbers?: MileageNumberArgs[];
};

export type TravelerInformationArgs = {
  id: number;
  name: string;
  email?: string;
  userId?: number;
  userName?: string;
  lastNameKana?: string;
  firstNameKana?: string;
  lastNameRoman?: string;
  firstNameRoman?: string;
  airSeat?: string;
  shinkansenSeat?: string;
  shinkansenSeatTypeText?: string;
  shinkansenTicketTypeText?: string;
  showRailwaySeat?: boolean;
  travelerInformationShinkansenTypes?: TravelerShinkansenTypeArgs[];
  shinkansenSeatType?: string;
  shinkansenTicketType?: string;
  exicNumber?: string;
  exicPassword?: string;
  mileageNumber?: string;
  mileageNumbers: MileageNumber[];
};

export const convertTravelerInformationResponseToArgs = (response: TravelerInformationResponseArgs) => {
  const args: TravelerInformationArgs = {
    id: response.id,
    name: response.name,
    email: response.email,
    userId: response.user_id,
    userName: response.user_name,
    lastNameKana: response.last_name_kana,
    firstNameKana: response.first_name_kana,
    lastNameRoman: response.last_name_roman,
    firstNameRoman: response.first_name_roman,
    airSeat: response.air_seat,
    shinkansenSeat: response.shinkansen_seat,
    shinkansenSeatTypeText: response.shinkansen_seat_type_text,
    shinkansenTicketTypeText: response.shinkansen_ticket_type_text,
    showRailwaySeat: response.show_railway_seat,
    travelerInformationShinkansenTypes:
      response.traveler_information_shinkansen_types?.map(type => {
        return convertTravelerShinkansenTypeResponseToArgs(type);
      }) || [],
    shinkansenSeatType: response.shinkansen_seat_type,
    shinkansenTicketType: response.shinkansen_ticket_type,
    exicNumber: response.exic_number,
    exicPassword: response.exic_password,
    mileageNumber: response.mileage_number,
    mileageNumbers: response.mileage_numbers
      ? response.mileage_numbers.map(mileageNumber => new MileageNumber(mileageNumber))
      : []
  };
  return args;
};

export default class TravelerInformation {
  @observable id: number;
  @observable name: string;
  @observable email?: string;
  @observable userId?: number;
  @observable userName?: string;
  @observable lastNameKana?: string;
  @observable firstNameKana?: string;
  @observable lastNameRoman?: string;
  @observable firstNameRoman?: string;
  @observable airSeat?: string;
  @observable shinkansenSeat?: string;
  @observable shinkansenSeatTypeText?: string;
  @observable shinkansenTicketTypeText?: string;
  @observable showRailwaySeat?: boolean;
  @observable travelerInformationShinkansenTypes?: TravelerShinkansenType[];
  @observable shinkansenSeatType?: string;
  @observable shinkansenTicketType?: string;
  @observable exicNumber?: string;
  @observable exicPassword?: string;
  @observable mileageNumber?: string;
  @observable mileageNumbers: MileageNumber[];

  constructor(args: TravelerInformationArgs) {
    this.id = args.id;
    this.name = args.name;
    this.email = args.email;
    this.userId = args.userId;
    this.userName = args.userName;
    this.lastNameKana = args.lastNameKana;
    this.firstNameKana = args.firstNameKana;
    this.lastNameRoman = args.lastNameRoman;
    this.firstNameRoman = args.firstNameRoman;
    this.airSeat = args.airSeat;
    this.shinkansenSeat = args.shinkansenSeat;
    this.shinkansenSeatTypeText = args.shinkansenSeatTypeText;
    this.shinkansenTicketTypeText = args.shinkansenTicketTypeText;
    this.showRailwaySeat = args.showRailwaySeat;
    this.travelerInformationShinkansenTypes =
      args.travelerInformationShinkansenTypes?.map(type => new TravelerShinkansenType(type)) || [];
    this.shinkansenSeatType = args.shinkansenSeatType;
    this.shinkansenTicketType = args.shinkansenTicketType;
    this.exicNumber = args.exicNumber;
    this.exicPassword = args.exicPassword;
    this.mileageNumber = args.mileageNumber;
    this.mileageNumbers = args.mileageNumbers;
  }

  get travelerName() {
    if (this.userName) {
      return this.userName;
    }

    if (this.lastNameKana && this.firstNameKana) {
      return `${this.lastNameKana} ${this.firstNameKana}`;
    }

    if (this.lastNameRoman && this.firstNameRoman) {
      return `${this.lastNameRoman} ${this.firstNameRoman}`;
    }

    return '';
  }

  getShinkansenSeatTypeText(elementIndex: number | null = null) {
    if (
      elementIndex != null &&
      this.travelerInformationShinkansenTypes &&
      this.travelerInformationShinkansenTypes[elementIndex]
    ) {
      const type = this.travelerInformationShinkansenTypes[elementIndex];
      return type.getShinkansenSeatTypeText() || '';
    }

    return this.shinkansenSeatTypeText || '';
  }

  getShinkansenTicketTypeText(elementIndex: number | null = null) {
    if (
      elementIndex != null &&
      this.travelerInformationShinkansenTypes &&
      this.travelerInformationShinkansenTypes[elementIndex]
    ) {
      const type = this.travelerInformationShinkansenTypes[elementIndex];
      return type.getShinkansenTicketTypeText() || '';
    }

    return this.shinkansenTicketTypeText || '';
  }

  travelerShinkansenType(elementIndex: number | null = null) {
    const list: string[] = [];

    if (this.shinkansenTicketType) {
      list.push(`券種: ${this.getShinkansenTicketTypeText(elementIndex)}`);
    }

    if (this.shinkansenSeatType) {
      list.push(`座席: ${this.getShinkansenSeatTypeText(elementIndex)}`);
    }

    if (this.shinkansenSeat) {
      list.push(`座席希望: ${this.shinkansenSeat}`);
    }

    if (this.exicNumber && this.shinkansenTicketType === 'ex') {
      list.push(`EXIC会員ID: ${this.exicNumber}`);
    }

    if (this.exicPassword && this.shinkansenTicketType === 'ex') {
      list.push(`EXICパスワード: ${this.exicPassword.replace(/./g, '*')}`);
    }

    return `(${this.travelerName}) ${list.join(', ')}`;
  }

  get travelerAirlineTypeDomestic() {
    const list: string[] = [];

    if (this.airSeat) {
      list.push(`座席希望: ${this.airSeat}`);
    }

    if ((this.mileageNumber && !this.userId) || this.mileageNumbers.length > 0) {
      list.push(`マイル番号: ${this.travelerMileageNumberDomestic}`);
    }

    return `(${this.travelerName}) ${list.join(', ')}`;
  }

  get travelerAirlineTypeForeign() {
    const list: string[] = [];

    if (this.airSeat) {
      list.push(`座席希望: ${this.airSeat}`);
    }

    if (this.mileageNumber || this.mileageNumbers.length > 0) {
      list.push(`マイル番号: ${this.travelerMileageNumberForeign}`);
    }

    return `(${this.travelerName}) ${list.join(', ')}`;
  }

  get travelerMileageNumberForeign() {
    const list: string[] = [];

    if (this.mileageNumber) {
      list.push(this.mileageNumber);
    }

    this.mileageNumbers?.forEach(mileageNumber => {
      list.push(`(${mileageNumber.carrier}) ${mileageNumber.number}`);
    });

    return list.join('、');
  }

  get travelerMileageNumberDomestic() {
    const list: string[] = [];

    // 社員マスタから選択した(userIdを持っている)場合、mileageNumber(手入力)は表示しない
    if (this.mileageNumber && !this.userId) {
      list.push(this.mileageNumber);
    }

    this.mileageNumbers?.forEach(mileageNumber => {
      list.push(`(${mileageNumber.carrier}) ${mileageNumber.number}`);
    });

    return list.join('、');
  }
}
